/**
 * ConcernFlagModal Component
 *
 * "I'm Concerned" user reporting modal for flagging concerning content.
 * Allows users to report:
 * - Self-harm or crisis indicators
 * - Harmful or dangerous content
 * - Other concerns
 *
 * Design Principles:
 * - LOW-FRICTION: Structured categories + optional context
 * - PRIVACY-RESPECTING: Only metadata stored, never message content
 * - SUPPORTIVE: Always shows resources after submission
 * - ACCESSIBLE: WCAG AAA compliant
 *
 * Accessibility:
 * - 48px minimum touch targets
 * - High contrast colors (7:1+ ratio)
 * - Screen reader labels
 * - Focus management
 *
 * Created: 2025-12-18
 * Phase: Moderation & Safety Infrastructure (Week 2)
 */

import {
  View,
  Text,
  TextInput,
  Pressable,
  Modal,
  StyleSheet,
  ViewStyle,
} from 'react-native';
import { createBoxShadow } from '../../lib/design-tokens';
// Use keyboard-controller's KeyboardAvoidingView for reliable behavior across all devices
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from '@dramadetector/shared';
import {
  useSafetyModals,
  ConcernFlagData,
} from '../../contexts/SafetyModalsContext';
// Import ModalType directly from source - re-export resolves to undefined on web
import { ModalType } from '../../contexts/safety-modals/types-constants';
import { captureEvent } from '../../lib/analytics';
import { authenticatedFetch } from '@dramadetector/api-client';
import { getEdgeFunctionUrl } from '../../lib/api-url';
import { getDeviceId } from '../../lib/device';

export type ConcernReason = 'self_harm' | 'harmful_content' | 'other';

export interface ConcernFlagModalProps {
  /**
   * Optional callback after report is submitted
   */
  onSubmitted?: () => void;
}

/**
 * ConcernFlagModal - User concern reporting with structured categories
 *
 * Flow:
 * 1. User selects reason category
 * 2. Optionally adds context (free text)
 * 3. Submits report → stored in user_reports table
 * 4. Shows success confirmation with resources
 */
export function ConcernFlagModal({ onSubmitted }: ConcernFlagModalProps) {
  const { t } = useTranslation('safety');
  const { state, closeModal } = useSafetyModals();
  const [selectedReason, setSelectedReason] = useState<ConcernReason | null>(null);
  const [context, setContext] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const isVisible = state.modals[ModalType.CONCERN_FLAG]?.isVisible ?? false;
  const modalData = state.modals[ModalType.CONCERN_FLAG]?.data as
    | ConcernFlagData
    | undefined;

  // CRITICAL FIX: Reset state when modal visibility changes (no setTimeout race condition)
  // Bug: setTimeout(300ms) caused race condition when modal reopened quickly
  useEffect(() => {
    if (!isVisible) {
      // Reset all state immediately when modal closes
      setSelectedReason(null);
      setContext('');
      setShowSuccess(false);
      setShowError(false);
      setIsSubmitting(false);
    }
  }, [isVisible]);

  const handleClose = useCallback(() => {
    closeModal(ModalType.CONCERN_FLAG);
    // State is now reset by useEffect when isVisible becomes false
  }, [closeModal]);

  const handleReasonSelect = (reason: ConcernReason) => {
    setSelectedReason(reason);
  };

  const handleSubmit = useCallback(async () => {
    if (!selectedReason || isSubmitting) return;

    setIsSubmitting(true);
    setShowError(false); // Reset error state on new submit attempt

    try {
      const deviceId = await getDeviceId();

      // Determine priority based on reason
      let priority: string;
      if (selectedReason === 'self_harm') {
        priority = 'urgent';
      } else if (selectedReason === 'harmful_content') {
        priority = 'high';
      } else {
        priority = 'medium';
      }

      // Submit concern report via edge function
      const response = await authenticatedFetch(getEdgeFunctionUrl('submit-concern'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_id: deviceId,
          report_reason: selectedReason,
          report_context: context.trim() || null,
          source_analysis_id: modalData?.analysisId || null,
          priority,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[ConcernFlagModal] Failed to submit report:', errorData);
        throw new Error((errorData as { error?: string }).error || 'Failed to submit concern');
      }

      // Track event
      captureEvent('Safety_ConcernFlagged', {
        reason: selectedReason,
        has_analysis_id: !!modalData?.analysisId,
        has_context: context.trim().length > 0,
      });

      setShowSuccess(true);
      onSubmitted?.();
    } catch (error) {
      console.error('[ConcernFlagModal] Submit error:', error);
      // MEDIUM FIX: Show error state instead of fake success
      // Bug: Previously showed success UI even on failure, misleading users
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedReason, isSubmitting, context, modalData?.analysisId, onSubmitted]);

  const reasonOptions = useMemo((): { key: ConcernReason; label: string }[] => [
    { key: 'self_harm', label: t('concern_flag.reason_self_harm') },
    { key: 'harmful_content', label: t('concern_flag.reason_harmful') },
    { key: 'other', label: t('concern_flag.reason_other') },
  ], [t]);

  // Error state - shown when submission fails
  if (showError) {
    return (
      <Modal
        visible={isVisible}
        animationType="fade"
        transparent
        statusBarTranslucent
        onRequestClose={handleClose}
      >
        <View style={styles.overlay}>
          <View style={styles.modal}>
            <View style={styles.successContainer}>
              <Text style={styles.successIcon}>⚠️</Text>
              <Text style={styles.successTitle}>
                {t('concern_flag.error_title', { defaultValue: 'Unable to Submit' })}
              </Text>
              <Text style={styles.successMessage}>
                {t('concern_flag.error_message', { defaultValue: 'Something went wrong. Please try again later.' })}
              </Text>
              <Pressable
                onPress={handleClose}
                style={[styles.successButton, styles.errorButton]}
                accessibilityRole="button"
                accessibilityLabel={t('modal.ok')}
                testID="concern-error-close"
              >
                <Text style={styles.successButtonText}>{t('modal.ok')}</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    );
  }

  // Success state
  if (showSuccess) {
    return (
      <Modal
        visible={isVisible}
        animationType="fade"
        transparent
        statusBarTranslucent
        onRequestClose={handleClose}
      >
        <View style={styles.overlay}>
          <View style={styles.modal}>
            <View style={styles.successContainer}>
              <Text style={styles.successIcon}>💙</Text>
              <Text style={styles.successTitle}>
                {t('concern_flag.success_title')}
              </Text>
              <Text style={styles.successMessage}>
                {t('concern_flag.success_message')}
              </Text>
              <Pressable
                onPress={handleClose}
                style={styles.successButton}
                accessibilityRole="button"
                accessibilityLabel={t('modal.ok')}
                testID="concern-success-close"
              >
                <Text style={styles.successButtonText}>{t('modal.ok')}</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <Modal
      visible={isVisible}
      animationType="fade"
      transparent
      statusBarTranslucent
      onRequestClose={handleClose}
    >
      <KeyboardAvoidingView
        style={styles.overlay}
        behavior="padding"
      >
        <View style={styles.modal}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.icon}>🚩</Text>
            <Text style={styles.title}>{t('concern_flag.modal_title')}</Text>
          </View>

          {/* Subtitle */}
          <Text style={styles.subtitle}>{t('concern_flag.modal_subtitle')}</Text>

          {/* Reason Selection */}
          <View style={styles.reasonContainer}>
            {reasonOptions.map((option) => (
              <Pressable
                key={option.key}
                onPress={() => handleReasonSelect(option.key)}
                style={[
                  styles.reasonOption,
                  selectedReason === option.key && styles.reasonOptionSelected,
                ]}
                accessibilityRole="radio"
                accessibilityState={{ selected: selectedReason === option.key }}
                accessibilityLabel={option.label}
                testID={`concern-reason-${option.key}`}
              >
                <View
                  style={[
                    styles.radioCircle,
                    selectedReason === option.key && styles.radioCircleSelected,
                  ]}
                >
                  {selectedReason === option.key && (
                    <View style={styles.radioInner} />
                  )}
                </View>
                <Text
                  style={[
                    styles.reasonText,
                    selectedReason === option.key && styles.reasonTextSelected,
                  ]}
                >
                  {option.label}
                </Text>
              </Pressable>
            ))}
          </View>

          {/* Context Input */}
          <TextInput
            value={context}
            onChangeText={setContext}
            placeholder={t('concern_flag.context_placeholder')}
            placeholderTextColor="#9CA3AF"
            style={styles.contextInput}
            multiline
            numberOfLines={3}
            maxLength={500}
            accessibilityLabel={t('concern_flag.context_placeholder')}
            testID="concern-context-input"
          />

          {/* Buttons */}
          <View style={styles.buttonContainer}>
            <Pressable
              onPress={handleClose}
              style={styles.cancelButton}
              accessibilityRole="button"
              accessibilityLabel={t('concern_flag.cancel')}
              testID="concern-cancel-button"
            >
              <Text style={styles.cancelButtonText}>
                {t('concern_flag.cancel')}
              </Text>
            </Pressable>

            <Pressable
              onPress={handleSubmit}
              style={[
                styles.submitButton,
                (!selectedReason || isSubmitting) && styles.submitButtonDisabled,
              ]}
              disabled={!selectedReason || isSubmitting}
              accessibilityRole="button"
              accessibilityLabel={t('concern_flag.submit')}
              accessibilityState={{ disabled: !selectedReason || isSubmitting }}
              testID="concern-submit-button"
            >
              <Text
                style={[
                  styles.submitButtonText,
                  (!selectedReason || isSubmitting) &&
                    styles.submitButtonTextDisabled,
                ]}
              >
                {isSubmitting ? '...' : t('concern_flag.submit')}
              </Text>
            </Pressable>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

// Platform-aware modal shadow
const modalShadow = createBoxShadow({
  color: '#000',
  offsetX: 0,
  offsetY: 4,
  blurRadius: 16,
  opacity: 0.25,
  elevation: 8,
});

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    width: '100%',
    maxWidth: 400,
    padding: 24,
    ...modalShadow,
  } as ViewStyle,
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  icon: {
    fontSize: 28,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#111827',
    flex: 1,
  },
  subtitle: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
    marginBottom: 20,
  },
  reasonContainer: {
    gap: 10,
    marginBottom: 16,
  },
  reasonOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
    minHeight: 48, // Accessibility: 48px touch target
  },
  reasonOptionSelected: {
    borderColor: '#2563EB',
    backgroundColor: '#EFF6FF',
  },
  radioCircle: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioCircleSelected: {
    borderColor: '#2563EB',
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#2563EB',
  },
  reasonText: {
    fontSize: 15,
    color: '#374151',
    flex: 1,
  },
  reasonTextSelected: {
    color: '#1E40AF',
    fontWeight: '500',
  },
  contextInput: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    color: '#374151',
    backgroundColor: '#F9FAFB',
    minHeight: 80,
    textAlignVertical: 'top',
    marginBottom: 20,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  cancelButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#6B7280',
  },
  submitButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
    backgroundColor: '#DC2626',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  submitButtonDisabled: {
    backgroundColor: '#E5E7EB',
  },
  submitButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  submitButtonTextDisabled: {
    color: '#9CA3AF',
  },
  // Success state
  successContainer: {
    alignItems: 'center',
    padding: 16,
  },
  successIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  successTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 8,
    textAlign: 'center',
  },
  successMessage: {
    fontSize: 15,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 24,
  },
  successButton: {
    paddingVertical: 14,
    paddingHorizontal: 48,
    borderRadius: 12,
    backgroundColor: '#2563EB',
    minHeight: 48,
  },
  successButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  // Error state button (uses same layout as success, different color)
  errorButton: {
    backgroundColor: '#6B7280', // Gray for dismissive action
  },
});

export default ConcernFlagModal;
